/**
 * Dineo Design System - Color Palette
 *
 * CI palette built on Deep Forest Green (primary) and Amber Gold (secondary)
 * with Warm Sand neutrals. Includes semantic colors, order status,
 * and role-based accent extensions.
 *
 * Reference: docs/ci-colors.md
 * Source: Dineo_Brainstorming_Ideen.pptx
 */

const primary = {
  50: '#EDF6EE',
  100: '#D6ECDA',
  200: '#B4DBB6',
  300: '#8DC791',
  400: '#6AAE6F',
  500: '#4A8A52',
  600: '#3D7344',
  700: '#305C37',
  800: '#2A4D30',
  900: '#203C25',
  950: '#162A1B',
};

const secondary = {
  50: '#FFF8E7',
  100: '#FFEFC7',
  200: '#FFE0A3',
  300: '#FFD06B',
  400: '#FBAE40',
  500: '#FFAC01',
  600: '#D49200',
  700: '#B47A00',
  800: '#946300',
  900: '#7A5200',
};

const neutral = {
  50: '#FAF6ED',
  100: '#F0EDE6',
  200: '#E3DED1',
  300: '#C4BEB6',
  400: '#A49D94',
  500: '#867D72',
  600: '#6B6358',
  700: '#504A40',
  800: '#3D382F',
  900: '#2C2820',
  950: '#1A1710',
};

const success = {
  light: '#D6ECDA',
  main: '#3D7344',
  dark: '#203C25',
  contrastText: '#FFFFFF',
};

const warning = {
  light: '#FFEFC7',
  main: '#FFAC01',
  dark: '#7A5200',
  contrastText: '#1A1710',
};

const error = {
  light: '#FCDAD7',
  main: '#C4362A',
  dark: '#8B1A12',
  contrastText: '#FFFFFF',
};

const info = {
  light: '#D4EEF6',
  main: '#0989B1',
  dark: '#065A75',
  contrastText: '#FFFFFF',
};

const orderStatus = {
  PENDING_PAYMENT: '#8B5CF6',
  PLACED: '#0989B1',
  PREPARING: '#FFAC01',
  READY: '#3D7344',
  COMPLETED: '#6B6358',
  CANCELLED: '#C4362A',
};

const orderStatusBg = {
  PENDING_PAYMENT: '#EDE9FE',
  PLACED: '#D4EEF6',
  PREPARING: '#FFEFC7',
  READY: '#D6ECDA',
  COMPLETED: '#F0EDE6',
  CANCELLED: '#FCDAD7',
};

const roleAccent = {
  CUSTOMER: '#305C37',
  WAITER: '#0989B1',
  KITCHEN_STAFF: '#F26B43',
  CASHIER: '#8B5CF6',
  MANAGER: '#6366F1',
  ADMIN: '#2C2820',
};

const roleAccentBg = {
  CUSTOMER: '#EDF6EE',
  WAITER: '#D4EEF6',
  KITCHEN_STAFF: '#FDECE5',
  CASHIER: '#EDE9FE',
  MANAGER: '#EEF2FF',
  ADMIN: '#F0EDE6',
};

export const lightPalette = {
  mode: 'light',
  primary: {
    ...primary,
    main: '#305C37',
    light: '#3D7344',
    dark: '#203C25',
    contrastText: '#FFFFFF',
  },
  secondary: {
    ...secondary,
    main: '#FFAC01',
    contrastText: '#1A1710',
  },
  neutral,
  success,
  warning,
  error,
  info,
  background: {
    default: '#FAF6ED',
    paper: '#FFFFFF',
  },
  text: {
    primary: '#2C2820',
    secondary: '#6B6358',
    disabled: '#A49D94',
  },
  divider: '#E3DED1',
  brand: {
    forest: '#D6ECDA',
    forestDeep: '#B4DBB6',
    soft: 'rgba(255, 255, 255, 0.88)',
    shadow: 'rgba(22, 42, 27, 0.15)',
    glow: 'rgba(48, 92, 55, 0.25)',
    // Glass-variant page backgrounds (warm gastro palette)
    glassStart: '#FAF6ED',
    glassEnd: '#F5EEE2',
    glassForest: 'rgba(48, 92, 55, 0.10)',
    glassAmber: 'rgba(255, 172, 1, 0.06)',
  },
  orderStatus,
  orderStatusBg,
  roleAccent,
  roleAccentBg,
};

export const darkPalette = {
  mode: 'dark',
  primary: {
    ...primary,
    main: '#6AAE6F',
    light: '#8DC791',
    dark: '#3D7344',
    contrastText: '#FFFFFF',
  },
  secondary: {
    ...secondary,
    main: '#FBAE40',
    contrastText: '#1A1710',
  },
  neutral,
  success,
  warning,
  error,
  info,
  background: {
    default: '#1A1710',
    paper: '#2C2820',
  },
  text: {
    primary: '#FAF6ED',
    secondary: '#A49D94',
    disabled: '#6B6358',
  },
  divider: '#504A40',
  brand: {
    forest: 'rgba(106, 174, 111, 0.12)',
    forestDeep: 'rgba(106, 174, 111, 0.2)',
    soft: 'rgba(0, 0, 0, 0.5)',
    shadow: 'rgba(0, 0, 0, 0.4)',
    glow: 'rgba(106, 174, 111, 0.25)',
    // Glass-variant page backgrounds (warm gastro palette — dark mode)
    glassStart: '#1A1710',
    glassEnd: '#1A1710',
    glassForest: 'rgba(106, 174, 111, 0.06)',
    glassAmber: 'rgba(251, 174, 64, 0.04)',
  },
  orderStatus,
  orderStatusBg,
  roleAccent,
  roleAccentBg,
};
